import React, { useState } from "react";
import { Box, Grid2, Typography, Stepper, Step, StepLabel, Card, CardContent, StepConnector, Divider } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import GreenBlinkingDotLottie from "../../Components/Lotties/GreenBlinkingDotLottie";
import Step1SelectResourceType from "./Steps/Step1SelectResourceType";
import Step2FileUpload from "./Steps/Step2FileUpload";
import Step3DataPreview from "./Steps/Step3DataPreview";
import Step4ValidateData from "./Steps/Step4ValidateData";
import Step5UploadComplete from "./Steps/Step5UploadComplete";
import { dataImport, dataImportValidate, dataImportConfirmUpload } from "../../Api/ResourceOnboardingApi";

const steps = ['Upload Data', 'Data Validation', 'Upload Complete'];

const breadcrumbLinkSx = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
        opacity: 0.7
    }
};

// Custom StepIcon component using GreenBlinkingDotLottie for active and FiberManualRecordIcon for others
const CustomStepIcon = (props) => {
    const { active, completed } = props;

    // Active step: Use GreenBlinkingDotLottie
    if (active) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <GreenBlinkingDotLottie width={20} height={20} />
            </Box>
        );
    }

    // Completed step: Use FiberManualRecordIcon with #008A15 color
    // Inactive step: Use FiberManualRecordIcon with #A2A2A2 color
    return (
        <FiberManualRecordIcon
            sx={{
                color: completed ? '#008A15' : '#A2A2A2',
                fontSize: '20px'
            }}
        />
    );
};

// Custom connector styles - green for completed, gray for inactive
const CustomConnector = (props) => (
    <StepConnector
        {...props}
        sx={{
            '&.Mui-completed .MuiStepConnector-line': {
                borderColor: '#008A15',
                borderTopWidth: '2px',
                marginTop: '-3px',
                marginLeft: '-14px',
                marginRight: '-14px',
            },
            '&.Mui-active .MuiStepConnector-line': {
                borderColor: '#008A15',
                borderTopWidth: '2px',
                marginTop: '-3px',
                marginLeft: '-14px',
                marginRight: '-14px',
            },
            '& .MuiStepConnector-line': {
                borderColor: '#A2A2A2',
                borderTopWidth: '2px',
                marginTop: '-3px',
                marginLeft: '-14px',
                marginRight: '-14px',
            }
        }}
    />
);

const NewUploadMain = ({ onBack }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedResourceType, setSelectedResourceType] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [validatedData, setValidatedData] = useState(null);
    const [importUUID, setImportUUID] = useState(null);
    const [uploadSummaryData, setUploadSummaryData] = useState(null);

    const handleResourceTypeSelect = (resourceTypeData) => {
        setSelectedResourceType(resourceTypeData);
        setCurrentStep(2);
    };

    const handleFileSelect = (file) => {
        setSelectedFile(file);
    };

    const handleNextFromFileUpload = (file, csvPreviewData) => {
        // Move to next step (Data Preview)
        setPreviewData(csvPreviewData);
        setCurrentStep(3);
    };

    const handleValidate = async (data) => {

        // Upload the file using dataImport API
        const dataImportResponse = await dataImport(selectedResourceType?.type || 'INVENTORY', selectedFile);

        // Check for error response
        if (dataImportResponse?.responseStatus?.code && dataImportResponse?.responseStatus?.code !== 200) {
            // Return error to be handled by Step3
            throw new Error(dataImportResponse.responseStatus.msg || 'An error occurred during upload');
        }

        const uploadUUID = dataImportResponse?.response?.data?.importUUID || null;
        if (uploadUUID) {
            // Store the importUUID for later use
            setImportUUID(uploadUUID);

            const dataImportValidateResponse = await dataImportValidate(uploadUUID);

            // Check for error in validation response
            if (dataImportValidateResponse?.responseStatus?.code && dataImportValidateResponse?.responseStatus?.code !== 200) {
                throw new Error(dataImportValidateResponse.responseStatus.msg || 'An error occurred during validation');
            }

            const validationResponse = dataImportValidateResponse?.response?.data || {};

            // Store the validation data
            setValidatedData(validationResponse);

            // Move to validation step after successful upload
            setCurrentStep(4);
        }
    };

    const handleUpload = async (uploadData) => {
        // Call the confirm upload API
        const confirmResponse = await dataImportConfirmUpload(importUUID, uploadData.remark);

        // Check for error response
        if (confirmResponse?.responseStatus?.code && confirmResponse?.responseStatus?.code !== 200) {
            // Throw error to be handled by Step4
            throw new Error(confirmResponse.responseStatus.msg || 'An error occurred during final upload');
        }

        // Extract summary data from response
        const summaryData = confirmResponse?.response?.data;

        // Check if import status is FAILED even when API returns 200
        if (summaryData?.status === 'FAILED') {
            throw new Error('The import failed. Please validate the records and retry.');
        }

        setUploadSummaryData(summaryData);
        setCurrentStep(5);
    };

    const handleExitWorkflow = () => {
        setCurrentStep(1);
        setSelectedResourceType(null);
        setSelectedFile(null);
        setPreviewData(null);
        setValidatedData(null);
        setImportUUID(null);
        onBack();
    };

    // Back to choosing the import type, discarding the file picked for the previous type.
    const handleBackToTypeSelection = () => {
        setCurrentStep(1);
        setSelectedResourceType(null);
        setSelectedFile(null);
        setPreviewData(null);
    };

    // Chevron: one step back while nothing has been uploaded yet (type selection,
    // file upload, preview). From validation onward the upload exists on the server,
    // so going back leaves the workflow.
    const handleBack = () => {
        if (currentStep === 2) {
            handleBackToTypeSelection();
        } else if (currentStep === 3) {
            setSelectedFile(null);
            setPreviewData(null);
            setCurrentStep(2);
        } else {
            handleExitWorkflow();
        }
    };

    // "New Upload" in the breadcrumb returns to type selection until the file is uploaded.
    const canReturnToTypeSelection = Boolean(selectedResourceType) && currentStep <= 3;

    const getActiveStepIndex = () => {
        // Map currentStep to stepper index
        if (currentStep === 1 || currentStep === 2 || currentStep === 3) return 0; // Upload Data (includes resource selection, file upload, and preview)
        if (currentStep === 4) return 1; // Data Validation
        if (currentStep === 5) return 2; // Upload Complete
        return 0;
    };

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1:
                return <Step1SelectResourceType onSelect={handleResourceTypeSelect} />;
            case 2:
                return (
                    <Step2FileUpload
                        selectedResourceType={selectedResourceType}
                        onFileSelect={handleFileSelect}
                        onNext={handleNextFromFileUpload}
                    />
                );
            case 3:
                return (
                    <Step3DataPreview
                        selectedFile={selectedFile}
                        selectedResourceType={selectedResourceType}
                        previewData={previewData}
                        onValidate={handleValidate}
                        onBackToTable={onBack}
                    />
                );
            case 4:
                return (
                    <Step4ValidateData
                        selectedResourceType={selectedResourceType}
                        validationData={validatedData}
                        importUUID={importUUID}
                        onUpload={handleUpload}
                        onBackToTable={onBack}
                    />
                );
            case 5:
                return (
                    <Step5UploadComplete
                        selectedResourceType={selectedResourceType}
                        uploadData={uploadSummaryData}
                    />
                );
            default:
                return <Step1SelectResourceType onSelect={handleResourceTypeSelect} />;
        }
    };

    return (
        <Grid2 container flexDirection={'column'} gap={2}>
            {/* Breadcrumb Navigation */}
            <Grid2 container alignItems="center" gap={1}>
                <Box
                    component="button"
                    type="button"
                    aria-label="Back"
                    onClick={handleBack}
                    sx={{ ...breadcrumbLinkSx, border: 'none', background: 'none', p: 0 }}
                >
                    <ChevronLeftIcon style={{ width: 20, height: 20 }} />
                </Box>
                <Box onClick={handleExitWorkflow} sx={breadcrumbLinkSx}>
                    <Typography variant="fw500s16c000000" sx={{ color: '#9E9E9E', lineHeight: '28px' }}>
                        Uploaded Files/
                    </Typography>
                </Box>
                {selectedResourceType ? (
                    <>
                        <Box
                            onClick={canReturnToTypeSelection ? handleBackToTypeSelection : undefined}
                            sx={canReturnToTypeSelection ? breadcrumbLinkSx : undefined}
                        >
                            <Typography variant="fw500s16c000000" sx={{ color: '#9E9E9E', lineHeight: '28px' }}>
                                New Upload/
                            </Typography>
                        </Box>
                        <Typography variant="fw500s16c000000" sx={{ color: '#2E2E2E', lineHeight: '28px' }}>
                            {selectedResourceType.label}
                        </Typography>
                    </>
                ) : (
                    <Typography variant="fw500s16c000000" sx={{ color: '#2E2E2E', lineHeight: '28px' }}>
                        New Upload
                    </Typography>
                )}
            </Grid2>

            {/* Main Content Card */}
            <Card sx={{
                borderRadius: '12px',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
                border: '1px solid #F0F0F0'
            }}>
                <CardContent sx={{ p: 4 }}>
                    {/* Stepper */}
                    <Box sx={{ mb: 4, maxWidth: '50%', mx: 'auto' }}>
                        <Stepper
                            activeStep={getActiveStepIndex()}
                            alternativeLabel
                            connector={<CustomConnector />}
                            sx={{
                                '& .MuiStepLabel-labelContainer .MuiStepLabel-alternativeLabel': {
                                    mt: 1
                                }
                            }}
                        >
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel StepIconComponent={CustomStepIcon}>
                                        <Typography variant="fw500s14c000000">
                                            {label}
                                        </Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                    <Divider sx={{ my: 3, borderColor: '#E6E6E6' }} />

                    {/* Render Current Step */}
                    {renderCurrentStep()}
                </CardContent>
            </Card>
        </Grid2>
    );
};

export default NewUploadMain;
